import React, { useState, useEffect, useMemo } from 'react';
import { Activity, BarChart2, RefreshCw, AlertCircle, Database, Search } from 'lucide-react';

const App = () => {
  const [tickers, setTickers] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [useLiveBackend, setUseLiveBackend] = useState(false);
  const [error, setError] = useState(null);
  
  // View State
  const [view, setView] = useState('analyze'); // 'analyze' or 'database'
  const [dbSearch, setDbSearch] = useState('');

  // Sorting State
  const [sortConfig, setSortConfig] = useState({ key: 'rating', direction: 'descending' });

  // Mock data for demo mode
  const mockData = [
    {
      ticker: 'SEZL', company_name: 'Demo Corp.', price: 39.61, rating: 15,
      manual_scores: { M: 'Yes', V: 'No', C: 'Yes', F: 'Yes' },
      ichimoku_signals: { weekly: 'Hold', daily: 'Strong Sell', hourly: 'Strong Sell' },
      scores: { YahooReports: 3, TargetPrice: 3, Dividend: 0, PE: 2, EPS: 1, QuarterlyRevision: 1, Options: 4, FCFYield: 1 },
      last_updated: '2023-12-01 10:00:00',
      streaks: { M: 4, V: 0, C: 1, F: 7 },
      changes: { M: 0, V: -1, C: 1, F: 0 }
    },
    {
      ticker: 'AIOT', company_name: 'Demo Corp.', price: 19.24, rating: 14,
      manual_scores: { M: 'Yes', V: 'Yes', C: 'No', F: 'Yes' },
      ichimoku_signals: { weekly: 'Hold', daily: 'Hold', hourly: 'Strong Sell' },
      scores: { YahooReports: 3, TargetPrice: 3, Dividend: 0, PE: 1, EPS: 1, QuarterlyRevision: 0, Options: 2, FCFYield: 4 },
      last_updated: '2023-12-01 10:05:00',
      streaks: { M: 2, V: 1, C: 0, F: 3 },
      changes: { M: 0, V: 1, C: 0, F: 0 }
    }
  ];

  // Fetch History when entering Database view
  useEffect(() => {
    if (view === 'database' && useLiveBackend) {
      fetchHistory();
    } else if (view === 'database' && !useLiveBackend) {
        setResults(mockData);
    }
  }, [view, useLiveBackend]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await fetch('/stock_evaluator_api.php?action=history');
      if (!response.ok) throw new Error('Failed to fetch history');
      const data = await response.json();
      
      if (Array.isArray(data)) {
        setResults(data);
      } else {
        setResults([]);
        console.error("API returned non-array data:", data);
      }
    } catch (err) {
      setError("Could not load database. Ensure backend is running.");
      setResults([]);
    }
    setLoading(false);
  };

  const handleAnalyze = async () => {
    if (!tickers.trim()) return;
    setLoading(true);
    setError(null);
    setResults([]);
    
    const tickerList = tickers.split(',').map(t => t.trim());

    if (useLiveBackend) {
      try {
        const response = await fetch('/stock_evaluator_api.php?action=analyze', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tickers: tickerList }),
        });
        if (!response.ok) throw new Error('Failed to connect to backend.');
        const data = await response.json();
        
        if (Array.isArray(data)) {
            setResults(data);
        } else {
            setResults([]);
            if (data.error) setError(data.error);
        }
      } catch (err) {
        setError("Could not connect to Python backend. Ensure 'stock_evaluator_api.py' is running on port 5000.");
      }
    } else {
      setTimeout(() => {
        const demoResults = tickerList.map(t => {
          return {
            ticker: t.toUpperCase(),
            company_name: 'Demo Corp.',
            price: (Math.random() * 200 + 10).toFixed(2),
            rating: Math.floor(Math.random() * 10) + 5,
            manual_scores: { M: 'Yes', V: 'Yes', C: 'No', F: 'Yes' },
            ichimoku_signals: { weekly: 'Hold', daily: 'Sell', hourly: 'Sell' },
            scores: { YahooReports: 3, TargetPrice: 3, Dividend: 0, PE: 1, EPS: 1, QuarterlyRevision: 1, Options: 2, FCFYield: 1 },
            last_updated: new Date().toLocaleString()
          };
        });
        setResults(demoResults.sort((a, b) => b.rating - a.rating));
        setLoading(false);
      }, 1000);
    }
    setLoading(false);
  };

  // --- SORTING LOGIC ---

  const handleSort = (key) => {
    let direction = 'descending';
    if (sortConfig.key === key && sortConfig.direction === 'descending') {
      direction = 'ascending';
    }
    setSortConfig({ key, direction });
  };

  const sortedResults = useMemo(() => {
    const safeResults = Array.isArray(results) ? results : [];

    let sortableItems = view === 'database' 
        ? safeResults.filter(r => r.ticker && r.ticker.includes(dbSearch.toUpperCase())) 
        : [...safeResults];

    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const getVal = (obj, path) => {
            if (!obj) return '';
            return path.split('.').reduce((acc, part) => (acc ? acc[part] : undefined), obj);
        };

        let valA = getVal(a, sortConfig.key);
        let valB = getVal(b, sortConfig.key);

        if (valA === undefined || valA === null || valA === '-') valA = -999999;
        if (valB === undefined || valB === null || valB === '-') valB = -999999;

        if (typeof valA === 'string' && !isNaN(parseFloat(valA))) valA = parseFloat(valA);
        if (typeof valB === 'string' && !isNaN(parseFloat(valB))) valB = parseFloat(valB);

        if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [results, sortConfig, view, dbSearch]);

  // --- UI COMPONENTS ---

  const SortHeader = ({ label, sortKey, align = "center", color = "text-slate-400" }) => {
    const isActive = sortConfig.key === sortKey;
    return (
        <th 
            className={`p-4 border-b border-slate-700 ${color} cursor-pointer hover:bg-slate-800 transition select-none text-${align}`}
            onClick={() => handleSort(sortKey)}
        >
            <div className={`flex items-center gap-1 ${align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start"}`}>
                {label}
                {isActive && (
                    <span className="text-xs ml-1 text-slate-200">
                        {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                    </span>
                )}
            </div>
        </th>
    );
  };

  // --- STYLING HELPERS ---
  const getScoreColor = (score) => {
    if (score >= 10) return 'bg-green-600 text-white';
    if (score >= 5) return 'bg-blue-600 text-white';
    if (score >= 0) return 'bg-yellow-600 text-white';
    return 'bg-red-600 text-white';
  };
  const getSignalColorClass = (signal) => {
    const s = signal?.toLowerCase() || '';
    if (s.includes('strong buy')) return 'text-green-400 font-extrabold';
    if (s.includes('strong sell')) return 'text-red-500 font-extrabold';
    if (s.includes('buy')) return 'text-green-300 font-medium';
    if (s.includes('sell')) return 'text-red-300 font-medium';
    return 'text-slate-400 font-normal';
  };
  const getComponentScoreColor = (val) => {
    if (val > 0) return 'text-green-400';
    if (val < 0) return 'text-red-400';
    return 'text-slate-500';
  };
  const getMVCFColor = (val) => {
      if (val === 'Yes') return 'text-green-400 font-bold';
      if (val === 'No') return 'text-red-400 font-bold';
      return 'text-slate-600';
  };

  // Renders a single MVCF cell with streak count and flip arrow
  const MVCFCell = ({ val, streak, change }) => {
    const textColor = val === 'Yes' ? 'text-green-400' : val === 'No' ? 'text-red-400' : 'text-slate-600';
    const arrow = change === 1
      ? <span className="text-green-300 font-normal ml-0.5">↑</span>
      : change === -1
      ? <span className="text-red-300 font-normal ml-0.5">↓</span>
      : null;
    return (
      <td className="p-4 text-center">
        <div className="flex flex-col items-center leading-tight">
          <span className={`font-bold ${textColor}`}>{val || '-'}{arrow}</span>
          {streak > 0 && <span className="text-xs text-slate-500 mt-0.5">{streak}×</span>}
        </div>
      </td>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 p-4 font-sans flex flex-col">
      <div className="max-w-[1600px] w-full mx-auto space-y-4 flex-grow flex flex-col">
        {/* Header */}
        <header className="flex flex-col md:flex-row justify-between items-center pb-4 border-b border-slate-700 gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 text-blue-400">
              <Activity className="h-6 w-6" /> 
              Trader Jim's Evaluator <span className="text-xs bg-blue-900 text-blue-200 px-2 py-1 rounded ml-2">v3.1 Sorting (Safe)</span>
            </h1>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex bg-slate-800 rounded-lg p-1 border border-slate-700">
                <button 
                    onClick={() => setView('analyze')}
                    className={`px-4 py-1.5 rounded flex items-center gap-2 text-sm font-bold transition ${view === 'analyze' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
                >
                    <RefreshCw className="h-4 w-4" /> Analyze
                </button>
                <button 
                    onClick={() => setView('database')}
                    className={`px-4 py-1.5 rounded flex items-center gap-2 text-sm font-bold transition ${view === 'database' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-white'}`}
                >
                    <Database className="h-4 w-4" /> Database
                </button>
            </div>

            <div className="flex items-center gap-2 bg-slate-800 p-1.5 rounded-lg border border-slate-700">
              <button onClick={() => setUseLiveBackend(false)} className={`px-3 py-1 rounded text-xs font-medium transition ${!useLiveBackend ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}>Demo</button>
              <button onClick={() => setUseLiveBackend(true)} className={`px-3 py-1 rounded text-xs font-medium transition ${useLiveBackend ? 'bg-green-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}>Live PHP</button>
            </div>
          </div>
        </header>

        {/* Controls */}
        {view === 'analyze' ? (
            <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 flex flex-col md:flex-row gap-4">
                <input 
                  type="text" 
                  placeholder="Enter tickers: SEZL, AIOT, TSLA..." 
                  className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase tracking-wider placeholder-slate-600"
                  value={tickers}
                  onChange={(e) => setTickers(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAnalyze()}
                />
                <button 
                  onClick={handleAnalyze}
                  disabled={loading}
                  className={`px-6 py-2 rounded-lg font-bold flex items-center justify-center gap-2 shadow-lg transition active:scale-95 ${loading ? 'bg-slate-600 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}
                >
                  {loading ? <RefreshCw className="animate-spin h-5 w-5" /> : <BarChart2 className="h-5 w-5" />}
                  {loading ? 'Processing...' : 'Analyze Tickers'}
                </button>
            </div>
        ) : (
            <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 flex flex-col md:flex-row gap-4 items-center">
                <div className="flex-1 flex items-center gap-2 text-purple-300 font-bold">
                    <Database className="h-5 w-5" />
                    <span>Database View ({sortedResults.length} records)</span>
                </div>
                <div className="relative">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
                    <input 
                        type="text" 
                        placeholder="Search Database..." 
                        className="pl-9 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 uppercase tracking-wider placeholder-slate-600"
                        value={dbSearch}
                        onChange={(e) => setDbSearch(e.target.value)}
                    />
                </div>
                <button onClick={fetchHistory} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white font-medium">
                    Refresh DB
                </button>
            </div>
        )}

        {error && (
            <div className="p-3 bg-red-900/50 border border-red-700 rounded-lg flex items-center gap-3 text-red-200 text-sm">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
        )}

        {/* Data Grid Table */}
        <div className="flex-grow bg-slate-800 rounded-xl border border-slate-700 overflow-hidden flex flex-col shadow-2xl">
            <div className="overflow-x-auto flex-grow">
                <table className="w-full text-left border-collapse whitespace-nowrap">
                    <thead className="bg-slate-900 text-xs uppercase font-bold sticky top-0 z-10">
                        <tr>
                            <SortHeader label="Ticker" sortKey="ticker" align="left" />
                            <SortHeader label="M" sortKey="manual_scores.M" color="text-purple-400" />
                            <SortHeader label="V" sortKey="manual_scores.V" color="text-purple-400" />
                            <SortHeader label="C" sortKey="manual_scores.C" color="text-purple-400" />
                            <SortHeader label="F" sortKey="manual_scores.F" color="text-purple-400" />
                            <SortHeader label="Week.I.Rec" sortKey="ichimoku_signals.weekly" color="text-blue-400" />
                            <SortHeader label="Day.I.Rec" sortKey="ichimoku_signals.daily" color="text-blue-400" />
                            <SortHeader label="Hour.I.Rec" sortKey="ichimoku_signals.hourly" color="text-blue-400" />
                            <SortHeader label="Rtng" sortKey="rating" />
                            <SortHeader label="Y_Rpts" sortKey="scores.YahooReports" />
                            <SortHeader label="Tgt P" sortKey="scores.TargetPrice" />
                            <SortHeader label="Div" sortKey="scores.Dividend" />
                            <SortHeader label="PE" sortKey="scores.PE" />
                            <SortHeader label="EPS" sortKey="scores.EPS" />
                            <SortHeader label="QRev" sortKey="scores.QuarterlyRevision" />
                            <SortHeader label="Options" sortKey="scores.Options" />
                            <SortHeader label="FCFY" sortKey="scores.FCFYield" />
                            <SortHeader label="Updated" sortKey="last_updated" align="right" />
                        </tr>
                    </thead>
                    <tbody className="text-sm divide-y divide-slate-700/50">
                        {sortedResults.length === 0 && !loading && (
                             <tr>
                                <td colSpan="19" className="p-10 text-center text-slate-500 italic">
                                    {view === 'analyze' ? 'No data loaded. Enter tickers above.' : 'Database empty.'}
                                </td>
                             </tr>
                        )}
                        {sortedResults.map((stock) => (
                            <tr key={stock.ticker} className="hover:bg-slate-700/50 transition duration-75">
                                <td className="p-4 font-bold sticky left-0 bg-slate-800 z-10 border-r border-slate-700/50 shadow-xl group-hover:bg-slate-700">
                                    <div className="flex flex-col">
                                        <span className="text-base text-white">{stock.ticker}</span>
                                        <span className="text-10px text-slate-400 truncate max-w-[120px]">{stock.company_name}</span>
                                        <span className="text-xs text-slate-500 font-mono mt-1">${stock.price}</span>
                                    </div>
                                </td>
                                <MVCFCell val={stock.manual_scores?.M} streak={stock.streaks?.M} change={stock.changes?.M} />
                                <MVCFCell val={stock.manual_scores?.V} streak={stock.streaks?.V} change={stock.changes?.V} />
                                <MVCFCell val={stock.manual_scores?.C} streak={stock.streaks?.C} change={stock.changes?.C} />
                                <MVCFCell val={stock.manual_scores?.F} streak={stock.streaks?.F} change={stock.changes?.F} />

                                <td className={`p-4 text-center border-l border-slate-700 ${getSignalColorClass(stock.ichimoku_signals?.weekly)}`}>{stock.ichimoku_signals?.weekly || '-'}</td>
                                <td className={`p-4 text-center ${getSignalColorClass(stock.ichimoku_signals?.daily)}`}>{stock.ichimoku_signals?.daily || '-'}</td>
                                <td className={`p-4 text-center ${getSignalColorClass(stock.ichimoku_signals?.hourly)}`}>{stock.ichimoku_signals?.hourly || '-'}</td>
                                
                                <td className="p-4 text-center"><span className={`inline-block px-3 py-1 rounded font-bold min-w-[30px] ${getScoreColor(stock.rating)}`}>{stock.rating}</span></td>

                                <td className={`p-4 text-center font-bold border-l border-slate-700 ${getComponentScoreColor(stock.scores?.YahooReports)}`}>{stock.scores?.YahooReports}</td>
                                <td className={`p-4 text-center font-bold ${getComponentScoreColor(stock.scores?.TargetPrice)}`}>{stock.scores?.TargetPrice}</td>
                                <td className={`p-4 text-center font-bold ${getComponentScoreColor(stock.scores?.Dividend)}`}>{stock.scores?.Dividend}</td>
                                <td className={`p-4 text-center font-bold ${getComponentScoreColor(stock.scores?.PE)}`}>{stock.scores?.PE}</td>
                                <td className={`p-4 text-center font-bold ${getComponentScoreColor(stock.scores?.EPS)}`}>{stock.scores?.EPS}</td>
                                <td className={`p-4 text-center font-bold ${getComponentScoreColor(stock.scores?.QuarterlyRevision)}`}>{stock.scores?.QuarterlyRevision}</td>
                                <td className={`p-4 text-center font-bold ${getComponentScoreColor(stock.scores?.Options)}`}>{stock.scores?.Options !== undefined ? stock.scores.Options : '-'}</td>
                                <td className={`p-4 text-center font-bold ${getComponentScoreColor(stock.scores?.FCFYield)}`}>{stock.scores?.FCFYield}</td>
                                
                                <td className="p-4 text-right text-xs text-slate-500 font-mono">
                                    {stock.last_updated ? (
                                        <>
                                            {stock.last_updated.split(' ')[0]} <br/> {stock.last_updated.split(' ')[1]}
                                        </>
                                    ) : '-'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
      </div>
    </div>
  );
};

export default App;
